import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/context/CartContext";
import ImageLightbox, { ZoomHint } from "./ImageLightbox";
import { variantAvailable, firstAvailable, cartLabel } from "@/lib/merchStock";
import useShipping, { shippingLine } from "@/hooks/useShipping";
import Price, { saleCountdown } from "./Price";
import WishlistButton from "./WishlistButton";

export default function MerchDetailModal({ product, open, onClose }) {
  const { addItem, setOpen: setCartOpen } = useCart();
  const [color, setColor] = useState(product?.colors?.[0] || "");
  const [size, setSize] = useState(product?.sizes?.[0] || "");
  const [selectedImage, setSelectedImage] = useState("");
  const [lightbox, setLightbox] = useState(false);
  const ship = useShipping();

  useEffect(() => {
    const fa = firstAvailable(product);
    setColor(fa.color || "");
    setSize(fa.size || "");
    setSelectedImage("");
  }, [product?.id]);

  const available = variantAvailable(product, size, color);

  const gallery = useMemo(() => {
    if (!product) return [];
    const set = new Set();
    if (product.image) set.add(product.image);
    (product.images || []).forEach((u) => u && set.add(u));
    Object.values(product.variant_images || {}).forEach((u) => u && set.add(u));
    return Array.from(set);
  }, [product]);

  const currentImage = selectedImage || (product?.variant_images && color && product.variant_images[color]) || product?.image || gallery[0];

  const pickColor = (c) => { setColor(c); setSelectedImage(""); };

  if (!product) return null;

  const handleAdd = () => {
    if (product.colors?.length > 0 && !color) return toast.error("Pick a color first");
    if (product.sizes?.length > 0 && !size) return toast.error("Pick a size first");
    if (available <= 0) return toast.error("That size / color is sold out");
    addItem({ ...product, image: currentImage, price: product.effective_price ?? product.price }, cartLabel(size, color));
    toast.success(`Added ${product.title} to cart`);
    setCartOpen(true);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-auto"
        onPointerDownOutside={(e) => { if (lightbox) e.preventDefault(); }}
        onInteractOutside={(e) => { if (lightbox) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (lightbox) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-[#0C2E2B]">{product.title}</DialogTitle>
          <DialogDescription className="sr-only">Product details, colors, sizes and add to cart</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <button
              type="button"
              data-testid="merch-detail-image-btn"
              onClick={() => setLightbox(true)}
              className="relative block w-full rounded-md overflow-hidden border-2 border-zinc-200 bg-zinc-100 cursor-zoom-in group focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
            >
              <img
                key={currentImage}
                src={currentImage}
                alt={product.title}
                className="w-full h-64 md:h-72 object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                data-testid="merch-detail-main-image"
              />
              <ZoomHint />
            </button>
            {gallery.length > 1 && (
              <div className="mt-2 grid grid-cols-4 gap-1.5">
                {gallery.slice(0, 8).map((url, i) => (
                  <button
                    key={url}
                    data-testid={`merch-detail-thumb-${i}`}
                    onClick={() => {
                      const variantEntry = Object.entries(product.variant_images || {}).find(([, v]) => v === url);
                      if (variantEntry) pickColor(variantEntry[0]);
                      else setSelectedImage(url);
                    }}
                    className={`rounded overflow-hidden border-2 ${currentImage === url ? "border-[#FFDF00]" : "border-zinc-200"}`}
                  >
                    <img src={url} alt="" className="w-full h-14 object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col">
            <div className="flex items-start justify-between gap-2">
              <Price p={product} className="text-3xl" testId="merch-detail-price" />
              <WishlistButton product={product} size={size} color={color} testId="merch-detail-wishlist-btn" />
            </div>
            {product.on_sale && <Badge data-testid="merch-detail-sale-badge" className="mt-1 self-start bg-red-600 hover:bg-red-600 text-white">{saleCountdown(product) || "On sale"}</Badge>}
            <div className="text-[10px] text-zinc-500 mt-1" data-testid="merch-detail-shipping-line">{shippingLine(ship)}</div>
            {available <= 0 ? (
              <Badge data-testid="merch-detail-soldout" className="mt-2 self-start bg-zinc-800 text-white hover:bg-zinc-800">Sold out in this option</Badge>
            ) : available <= 5 ? (
              <Badge data-testid="merch-detail-stock-badge" className="mt-2 self-start bg-[#FFDF00] text-[#0C2E2B] hover:bg-[#FFDF00]">Only {available} left</Badge>
            ) : null}
            <p className="mt-3 text-sm text-zinc-700 leading-relaxed">{product.description}</p>

            {product.colors?.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-700 mb-2">
                  Color {color && <span className="text-zinc-500 font-normal normal-case tracking-normal">· {color}</span>}
                </div>
                <div className="flex flex-wrap gap-2" data-testid="merch-detail-color-list">
                  {product.colors.map((c) => {
                    const ok = variantAvailable(product, "", c) > 0;
                    return (
                      <button
                        key={c}
                        data-testid={`merch-detail-color-${c}`}
                        onClick={() => pickColor(c)}
                        disabled={!ok}
                        title={ok ? c : `${c} — sold out`}
                        className={`px-3 py-1.5 rounded-md border-2 text-xs font-bold transition-colors ${color === c ? "bg-[#0C2E2B] text-white border-[#0C2E2B]" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"} ${!ok ? "opacity-40 line-through cursor-not-allowed" : ""}`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {product.sizes?.length > 0 && (
              <div className="mt-4">
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-700 mb-2">
                  Size {size && <span className="text-zinc-500 font-normal normal-case tracking-normal">· {size}</span>}
                </div>
                <div className="flex flex-wrap gap-2" data-testid="merch-detail-size-list">
                  {product.sizes.map((s) => {
                    const ok = variantAvailable(product, s, color) > 0;
                    return (
                      <button
                        key={s}
                        data-testid={`merch-detail-size-${s}`}
                        onClick={() => setSize(s)}
                        disabled={!ok}
                        title={ok ? s : `${s} — sold out${color ? " in " + color : ""}`}
                        className={`min-w-[44px] px-2 py-1.5 rounded-md border-2 text-xs font-bold transition-colors ${size === s ? "bg-[#0C2E2B] text-white border-[#0C2E2B]" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"} ${!ok ? "opacity-40 line-through cursor-not-allowed" : ""}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <Button
              data-testid="merch-detail-add-btn"
              onClick={handleAdd}
              disabled={available <= 0}
              className="btn-kinetic rounded-md mt-6 py-5 text-sm disabled:opacity-50"
            >
              {available <= 0 ? "Sold out" : `Add to Cart · $${Number(product.effective_price ?? product.price).toFixed(2)}`}
            </Button>
          </div>
        </div>
      </DialogContent>
      <ImageLightbox
        images={gallery.length ? [currentImage, ...gallery.filter((u) => u !== currentImage)] : [currentImage]}
        startIndex={0}
        open={lightbox}
        onClose={() => setLightbox(false)}
        title={product.title}
      />
    </Dialog>
  );
}
